import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Star,
  Check,
  Truck,
  Shield,
  RotateCcw,
  Minus,
  Plus,
  ChevronRight,
} from "lucide-react";
import { PRODUCTS } from "../mock";
import { fetchProduct } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const ProductDetail = () => {
  const { slug } = useParams();
  const fallback = PRODUCTS.find((p) => p.slug === slug) || PRODUCTS[0];
  const [product, setProduct] = useState(fallback);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProduct(slug)
      .then((data) => {
        setProduct({
          ...data,
          compareAt: data.compare_at,
        });
      })
      .catch(() => {});
  }, [slug]);

  return (
    <main className="bg-[#FFF2EC]">
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-16">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-[#1A1410]/60 mb-6">
          <Link to="/" className="hover:text-[#DD4D2A]">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/" className="hover:text-[#DD4D2A]">
            Rebounders
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A1410]">{product.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-[#FFE4D6]">
              <img
                src={product.images[activeImg]}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-[#1A1410] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                {product.badge}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`relative aspect-square rounded-xl overflow-hidden border-2 ${
                    activeImg === i
                      ? "border-[#DD4D2A]"
                      : "border-transparent"
                  }`}
                >
                  <img
                    src={src}
                    alt={`${product.name} ${i}`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.35em] text-[#DD4D2A] font-semibold">
              {product.tag}
            </div>
            <h1 className="mt-2 font-serif-display text-3xl md:text-5xl font-semibold text-[#1A1410] leading-tight">
              {product.name}
            </h1>
            <div className="mt-4 flex items-center gap-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#DD4D2A] text-[#DD4D2A]"
                  />
                ))}
              </div>
              <span className="text-sm text-[#1A1410]/65">
                ({product.rating}) · {product.reviews.toLocaleString()}{" "}
                verified reviews
              </span>
            </div>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-serif-display text-4xl font-semibold">
                ${product.price}.00
              </span>
              <span className="text-lg text-[#1A1410]/45 line-through">
                ${(product.compareAt ?? product.compare_at)}.00
              </span>
              <span className="bg-[#DD4D2A] text-white text-xs font-bold uppercase tracking-wider px-2 py-1 rounded">
                {product.badge}
              </span>
            </div>

            <p className="mt-5 text-[#1A1410]/75 leading-relaxed">
              {product.shortDesc}
            </p>

            <ul className="mt-6 space-y-2.5">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check
                    className="w-4 h-4 text-[#DD4D2A] mt-0.5 flex-shrink-0"
                    strokeWidth={3}
                  />
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-[#1A1410]/15 rounded-full bg-white">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="p-3 hover:bg-[#1A1410]/5 rounded-l-full"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 font-semibold">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="p-3 hover:bg-[#1A1410]/5 rounded-r-full"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => {
                  addItem(product, qty);
                  toast.success("Added to cart");
                }}
                className="btn-coral flex-1 py-4 rounded-full text-sm uppercase"
              >
                Add to Cart — ${(product.price * qty).toFixed(2)}
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { icon: Truck, label: "Free Shipping" },
                { icon: Shield, label: "30-Day Guarantee" },
                { icon: RotateCcw, label: "Easy Returns" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 bg-white rounded-xl p-3 border border-[#1A1410]/10"
                >
                  <Icon className="w-4 h-4 text-[#DD4D2A]" />
                  <span className="text-xs font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
